import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle, Trash, Pencil, Plus, XCircle, ShoppingCart } from 'lucide-react';
import { printMovement } from '../print/printMovement';
import { Modal } from '../Modal/Modal';
import { api } from '../../services/api';
import { useUserPrefs } from '../context/UserPrefs';

// Módulo de Pedidos Pendientes para Almacén
// Lee solicitudes de SGI desde localStorage y permite aprobarlas.
// Al aprobar: registra asignaciones por ítems con materialId y descuenta stock.
export function PendingOrdersModule({ location = 'popayan', materials = [], people = [], warehouses = [], onApproveAssign }) {
  const { currentUser } = useUserPrefs();
  const STORAGE_KEY = 'sgi_solicitudes_pedido';
  const [orders, setOrders] = useState([]);
  const [convertingId, setConvertingId] = useState(null);
  const [statusMsg, setStatusMsg] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // copia editable del pedido
  const [isRejectOpen, setIsRejectOpen] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('Pendiente'); // Pendiente | Aprobado | Rechazado | Todos

  const warehouseId = useMemo(() => {
    if (location === 'santander-de-quilichao') return 'santander';
    return 'popayan';
  }, [location]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.sgi.requests.list({ location, limit: 500, order: 'desc' });
        const rows = Array.isArray(res?.data) ? res.data : [];
        const mapped = rows.map((r) => ({
          id: r.id,
          requesterId: r.requester_id ?? r.requesterId ?? '',
                requesterName: r.requester_name ?? r.requesterName ?? '',
                collaboratorId: r.collaborator_id ?? r.collaboratorId ?? '',
                collaboratorName: r.collaborator_name ?? r.collaboratorName ?? '',
                project: r.project ?? '',
          date: r.date ?? (r.created_at ? String(r.created_at).slice(0, 10) : ''),
          items: Array.isArray(r.items) ? r.items : [],
          notes: r.notes ?? '',
          status: r.status ?? 'Pendiente',
          location: r.location ?? location,
          createdAt: r.created_at ?? new Date().toISOString(),
          purchaseRequestId: r.purchase_request_id ?? null,
          purchaseRequestCode: r.purchase_request_code ?? null,
        }));
        setOrders(mapped);
      } catch (_) {
        try {
          const raw = localStorage.getItem(STORAGE_KEY);
          const list = raw ? JSON.parse(raw) : [];
          setOrders(Array.isArray(list) ? list : []);
        } catch {
          setOrders([]);
        }
      }
    })();
  }, [location]);

  const filteredByLocation = useMemo(() => {
    return (orders || []).filter((o) => {
      const st = (o.status || 'Pendiente');
      const matchStatus = statusFilter === 'Todos' ? true : st === statusFilter;
      return matchStatus && o.location === location;
    });
  }, [orders, location, statusFilter]);

  const findMaterialById = (id) => materials.find((m) => String(m.id) === String(id));
  const findPersonById = (id) => people.find((p) => String(p.id) === String(id));

  const normalize = (s) => String(s || '').toLowerCase().trim();
  const matchMaterialByDescription = (desc) => {
    const d = normalize(desc);
    // Coincidencia exacta con "nombre - marca"
    const exact = materials.find((m) => normalize(`${m.name}${m.brand ? ' - ' + m.brand : ''}`) === d);
    if (exact) return exact;
    // Búsqueda por inclusión de nombre, código o marca
    return materials.find((m) => [m.name, m.code, m.brand].some((v) => d.includes(normalize(v))));
  };

  const stockFor = (mat) => {
    if (!mat) return 0;
    const s = mat.stockByWarehouse && (mat.stockByWarehouse[warehouseId] ?? mat.stockByWarehouse[String(warehouseId)]);
    return typeof s === 'number' ? s : 0;
  };

  const persistOrders = (list) => {
    setOrders(list);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
  };

  const openReject = (order) => {
    if (!order) return;
    setRejecting(order);
    setRejectReason(order.rejectReason || '');
    setIsRejectOpen(true);
  };

  const closeReject = () => {
    setIsRejectOpen(false);
    setRejecting(null);
    setRejectReason('');
  };

  const confirmReject = async () => {
    if (!rejecting) return;
    try {
      await api.sgi.requests.upsert({ action: 'reject', id: rejecting.id, notes: rejectReason || rejecting.notes || '', location });
    } catch (_) {}
    const next = (orders || []).filter((o) => o.id !== rejecting.id);
    persistOrders(next);
    setStatusMsg(`Pedido ${rejecting.id} rechazado${rejectReason ? ' — ' + rejectReason : ''}.`);
    closeReject();
  };

  const approveOrder = async (order) => {
    if (!order) return;

    // Determinar responsable: Colaborador (prioridad) > Solicitante
    let responsible = null;
    if (order.collaboratorId) {
      responsible = findPersonById(order.collaboratorId);
    }
    if (!responsible && order.collaboratorName) {
      responsible = people.find((p) => normalize(p.name) === normalize(order.collaboratorName));
    }
    // Fallback al solicitante si no hay colaborador definido
    if (!responsible) {
      responsible = findPersonById(order.requesterId) || people.find((p) => normalize(p.name) === normalize(order.requesterName));
    }

    if (!responsible) {
      alert('No se encontró el responsable (colaborador o solicitante) activo para este pedido.');
      return;
    }

    const items = Array.isArray(order.items) ? order.items : [];
    const approvedItems = [];
    const errors = [];

    if (typeof onApproveAssign !== 'function') {
      alert('No está disponible la función de asignación automática.');
      return;
    }

    for (const it of items) {
      let mid = it.materialId;
      let mat = mid ? findMaterialById(mid) : null;
      if (!mat && it.description) {
        mat = matchMaterialByDescription(it.description);
        mid = mat?.id;
      }
      if (!mid || !mat) {
        const desc = String(it?.description || '').trim();
        errors.push(`No se pudo identificar material para: ${desc || 'ítem sin descripción'}`);
        continue;
      }
      if (!mat) {
        errors.push(`Material ${mid} no encontrado.`);
        continue;
      }
      const qty = parseInt(it.quantity) || 0;
      if (qty <= 0) continue;

      const assignData = {
        materialId: mat.id,
        responsibleDoc: responsible.id,
        quantity: qty,
        warehouseId,
        observations: `Aprobación pedido ${order.id}${order.approvalNotes ? ' — ' + order.approvalNotes : ''}`,
        brandName: '',
        serial: '',
        type: 'Asignación',
      };

      try {
        const res = await onApproveAssign(assignData);
        if (!res?.success) {
          errors.push(res?.message || `No se pudo asignar ${mat.name}.`);
        } else {
          approvedItems.push({
            materialName: mat.name,
            unit: mat.unit || it.unit || 'Unidad',
            quantity: qty,
            category: mat.category || mat.type || 'Material',
          });
        }
      } catch (e) {
        errors.push(`Error al asignar ${mat.name}.`);
      }
    }

    if (approvedItems.length === 0) {
      setStatusMsg(errors.length ? `No se pudo aprobar: ${errors.join(' | ')}` : 'No se pudo aprobar: no hay ítems asignables.');
      return;
    }
    if (errors.length > 0) {
      setStatusMsg(`No se aprobó el pedido ${order.id}: ${errors.join(' | ')}`);
      return;
    }

    // Actualizar estado del pedido
    try {
      await api.sgi.requests.upsert({ id: order.id, status: 'Aprobado', notes: order.approvalNotes || order.notes || '', location });
    } catch (_) {}
    const next = (orders || []).map((o) => {
      if (o.id !== order.id) return o;
      return { ...o, status: 'Aprobado', approvedAt: new Date().toISOString() };
    });
    persistOrders(next);

    // Recibo de impresión consolidado de asignación
    if (approvedItems.length > 0) {
      const wh = (warehouses || []).find(w => String(w.id) === String(warehouseId));
      const receipt = {
        type: 'Asignación',
        date: new Date().toLocaleString('es-CO'),
        responsibleName: responsible.name,
        responsibleDoc: responsible.id,
        warehouseId,
        warehouseName: wh?.name || warehouseId,
        items: approvedItems.map((ai) => ({ materialName: ai.materialName, unit: ai.unit, quantity: ai.quantity, category: ai.category })),
        observations: `Aprobación de pedido ${order.id}. ${order.approvalNotes || order.notes || ''}`,
        entryRef: `PED-${order.id}`,
      };
      if (responsible?.signature) {
        receipt.signatureData = responsible.signature;
      }
      try { await printMovement(receipt); } catch (e) {}
    }

    if (errors.length > 0) {
      setStatusMsg(`Pedido ${order.id} aprobado con observaciones: ${errors.join(' | ')}`);
    } else {
      setStatusMsg(`Pedido ${order.id} aprobado y asignado correctamente.`);
    }
  };

  // Abrir editor de pedido
  const openEdit = (order) => {
    if (!order) return;
    const items = Array.isArray(order.items) ? order.items.map((it) => ({ ...it })) : [];
    setEditing({ ...order, items });
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditing(null);
  };

  const updateItemField = (idx, field, value) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).slice();
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const setItemMaterial = (idx, materialId) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).slice();
      items[idx] = { ...items[idx], materialId: materialId ? parseInt(materialId) : undefined };
      return { ...prev, items };
    });
  };

  const suggestItemMaterial = (idx) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).slice();
      const it = items[idx];
      const mat = matchMaterialByDescription(it?.description || '');
      items[idx] = { ...it, materialId: mat?.id };
      return { ...prev, items };
    });
  };

  const addItemRow = () => {
    setEditing((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).slice();
      items.push({ description: '', quantity: 1, unit: 'Unidad', materialId: undefined });
      return { ...prev, items };
    });
  };

  const removeItemRow = (idx) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const items = (prev.items || []).slice();
      items.splice(idx, 1);
      return { ...prev, items };
    });
  };

  const saveEdits = async () => {
    if (!editing) return;
    try {
      await api.sgi.requests.upsert({
        id: editing.id,
        requester_id: editing.requesterId || null,
        requester_name: editing.requesterName || null,
        project: editing.project || '',
        date: editing.date || null,
        location,
        items: editing.items || [],
        notes: editing.approvalNotes || editing.notes || '',
        status: editing.status || 'Pendiente',
      });
    } catch (_) {}
    const next = (orders || []).map((o) => (o.id === editing.id ? { ...o, items: editing.items, approvalNotes: editing.approvalNotes } : o));
    persistOrders(next);
    setStatusMsg(`Pedido ${editing.id} actualizado.`);
  };

  // Convierte un pedido sin stock suficiente en una Solicitud de compra
  // (SC) de Contabilidad, con los ítems ya descritos en el pedido — evita
  // que alguien tenga que volver a escribirlos desde cero.
  const convertToPurchaseRequest = async (order) => {
    if (!order || order.purchaseRequestCode) return;
    if (!currentUser?.id) {
      alert('No se pudo identificar tu usuario para generar la solicitud de compra.');
      return;
    }
    const items = Array.isArray(order.items) ? order.items : [];
    const scItems = items
      .map((it) => {
        const mat = it.materialId ? findMaterialById(it.materialId) : null;
        return {
          description: String(it.description || mat?.name || '').trim(),
          quantity: Number(it.quantity) || 0,
          unit: it.unit || mat?.unit || 'Unidad',
          material_code: mat?.code || null,
          material_name: mat?.name || null,
          material_id: mat?.id || null,
        };
      })
      .filter((it) => it.description && it.quantity > 0);
    if (!scItems.length) {
      alert('El pedido no tiene ítems válidos (descripción + cantidad) para convertir.');
      return;
    }
    setConvertingId(order.id);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const resp = await api.accounting.purchaseRequests.upsert({
        location: order.location || location,
        area: 'Almacén',
        requester_user_id: String(currentUser.id),
        requester_name: currentUser.name || '',
        requester_email: currentUser.email || '',
        priority: 'Normal',
        required_date: today,
        purchase_type: 'Bienes',
        description: `Generado desde pedido pendiente #${order.id}`,
        items: scItems,
        status: 'Pendiente',
      });
      if (!resp?.ok) {
        alert(resp?.error || 'No fue posible crear la solicitud de compra.');
        return;
      }
      await api.sgi.requests.upsert({
        id: order.id,
        requester_id: order.requesterId || null,
        requester_name: order.requesterName || null,
        collaborator_id: order.collaboratorId || null,
        collaborator_name: order.collaboratorName || null,
        project: order.project || '',
        date: order.date || null,
        location: order.location || location,
        items: order.items || [],
        notes: order.notes || '',
        status: order.status || 'Pendiente',
        purchase_request_id: resp.id,
        purchase_request_code: resp.code,
      });
      const next = (orders || []).map((o) => (o.id === order.id ? { ...o, purchaseRequestId: resp.id, purchaseRequestCode: resp.code } : o));
      persistOrders(next);
      setStatusMsg(`Pedido ${order.id} convertido a solicitud de compra ${resp.code}.`);
    } catch (e) {
      alert('Error al convertir el pedido a solicitud de compra.');
    } finally {
      setConvertingId(null);
    }
  };

  const approveFromEdit = async () => {
    if (!editing) return;
    // guardar primero
    await saveEdits();
    // aprobar con los cambios
    await approveOrder(editing);
    closeEdit();
  };

  const whName = useMemo(() => {
    if (warehouseId === 'santander') return 'Santander de Quilichao';
    return 'Popayán';
  }, [warehouseId]);

  return (
    <div className="bg-white rounded-xl shadow border border-gray-200 p-5">
      <h3 className="text-lg font-semibold mb-2">Pedidos pendientes</h3>
      <p className="text-sm text-gray-600">Aprobar y asignar automáticamente ítems en {whName}.</p>
      {statusMsg && (
        <div className="mt-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded p-2">{statusMsg}</div>
      )}

      <div className="mt-3">
        <div className="flex items-center gap-2 mb-2">
          {['Pendiente', 'Aprobado', 'Rechazado', 'Todos'].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded border ${statusFilter === s ? 'bg-gray-800 text-white' : 'bg-white text-gray-700'}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto border rounded">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Fecha</th>
              <th className="px-3 py-2 text-left">Solicitante</th>
              <th className="px-3 py-2 text-left">Proyecto</th>
              <th className="px-3 py-2 text-left">Ítems</th>
              <th className="px-3 py-2 text-left">Con stock</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Motivo rechazo</th>
              <th className="px-3 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredByLocation.map((r) => {
              const requester = findPersonById(r.requesterId);
              const collaborator = findPersonById(r.collaboratorId);
              const items = Array.isArray(r.items) ? r.items : [];
              const withStock = items.filter((it) => !!it.materialId).length;
              return (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2">{r.date}</td>
                  <td className="px-3 py-2">
                    <div>{requester ? `${requester.name} (${requester.id})` : (r.requesterId || '-')}</div>
                    {r.collaboratorName && <div className="text-xs text-gray-500">Colab: {collaborator ? collaborator.name : r.collaboratorName}</div>}
                  </td>
                  <td className="px-3 py-2">{r.project || '-'}</td>
                  <td className="px-3 py-2">{items.length}</td>
                  <td className="px-3 py-2">{withStock}</td>
                  <td className="px-3 py-2">
                    {r.status}
                    {r.purchaseRequestCode && (
                      <div className="text-[11px] text-blue-700 mt-1">SC: {r.purchaseRequestCode}</div>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.status === 'Rechazado' ? (r.rejectReason || '-') : ''}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center gap-2 justify-end">
                      {r.status !== 'Aprobado' && (
                        <button type="button" onClick={() => openEdit(r)} className="inline-flex items-center gap-1 bg-indigo-600 text-white px-3 py-1 rounded">
                          <Pencil size={14} /> Editar
                        </button>
                      )}
                      {r.status === 'Pendiente' && (
                        <button type="button" onClick={() => approveOrder(r)} className="inline-flex items-center gap-1 bg-green-600 text-white px-3 py-1 rounded">
                          <CheckCircle size={14} /> Aprobar
                        </button>
                      )}
                      {r.status === 'Pendiente' && !r.purchaseRequestCode && (
                        <button
                          type="button"
                          onClick={() => convertToPurchaseRequest(r)}
                          disabled={convertingId === r.id}
                          className="inline-flex items-center gap-1 bg-amber-600 text-white px-3 py-1 rounded hover:bg-amber-700 disabled:opacity-50"
                        >
                          <ShoppingCart size={14} /> {convertingId === r.id ? 'Convirtiendo...' : 'Convertir a SC'}
                        </button>
                      )}
                      {r.status === 'Pendiente' && (
                        <button type="button" onClick={() => openReject(r)} className="inline-flex items-center gap-1 bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700">
                          <XCircle size={14} /> Rechazar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredByLocation.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-4 text-center text-gray-600">No hay pedidos para el filtro seleccionado.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de edición de pedido */}
      <Modal isOpen={isEditOpen} onClose={closeEdit} title={`Editar pedido #${editing?.id || ''}`} size="max-w-4xl">
        {editing && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <div>Fecha: {editing.date}</div>
              <div>Proyecto: {editing.project || '-'}</div>
              <div>Solicitante: {editing.requesterName || editing.requesterId}</div>
              {editing.collaboratorName && <div>Colaborador: {editing.collaboratorName}</div>}
            </div>

            <div className="overflow-x-auto border rounded">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left">Descripción</th>
                    <th className="px-3 py-2 text-left">Cantidad</th>
                    <th className="px-3 py-2 text-left">Unidad</th>
                    <th className="px-3 py-2 text-left">Material (stock)</th>
                    <th className="px-3 py-2 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {(editing.items || []).map((it, idx) => {
                    const selected = materials.find((m) => String(m.id) === String(it.materialId));
                    return (
                      <tr key={idx} className="border-t">
                        <td className="px-3 py-2">
                          <input
                            className="w-full border rounded px-2 py-1"
                            value={it.description || ''}
                            onChange={(e) => updateItemField(idx, 'description', e.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min={1}
                            className="w-24 border rounded px-2 py-1"
                            value={parseInt(it.quantity) || ''}
                            onChange={(e) => updateItemField(idx, 'quantity', parseInt(e.target.value) || 0)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            className="w-24 border rounded px-2 py-1"
                            value={it.unit || 'Unidad'}
                            onChange={(e) => updateItemField(idx, 'unit', e.target.value)}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <select
                            className="min-w-[280px] border rounded px-2 py-1"
                            value={it.materialId || ''}
                            onChange={(e) => setItemMaterial(idx, e.target.value)}
                          >
                            <option value="">-- Sin material --</option>
                            {materials.map((m) => (
                              <option key={m.id} value={m.id}>
                                {`${m.name}${m.brand ? ' - ' + m.brand : ''}${m.code ? ' (' + m.code + ')' : ''} — stock ${stockFor(m)}`}
                              </option>
                            ))}
                          </select>
                          {selected && (
                            <div className="text-[11px] text-gray-500 mt-1">Stock disponible: {stockFor(selected)}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center gap-2 justify-end">
                            <button type="button" className="text-indigo-700 hover:text-indigo-900" onClick={() => suggestItemMaterial(idx)}>Sugerir</button>
                            <button type="button" className="text-red-600 hover:text-red-800 inline-flex items-center gap-1" onClick={() => removeItemRow(idx)}>
                              <Trash size={14} /> Quitar
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Observaciones (quién autoriza)</label>
              <textarea
                className="w-full border rounded px-2 py-1 text-sm"
                rows={2}
                value={editing.approvalNotes || ''}
                onChange={(e) => setEditing((prev) => ({ ...prev, approvalNotes: e.target.value }))}
                placeholder="Ej: Autorizado por Nombre Apellido — observaciones"
              />
            </div>

            <div className="flex items-center justify-between">
              <button
                type="button"
                className="inline-flex items-center gap-1 bg-indigo-600 text-white px-3 py-1 rounded"
                onClick={addItemRow}
              >
                <Plus size={14} /> Agregar ítem
              </button>
              <div className="flex items-center justify-end gap-2">
                <button type="button" className="px-3 py-1 rounded border" onClick={closeEdit}>Cancelar</button>
                <button type="button" className="px-3 py-1 rounded bg-gray-800 text-white" onClick={saveEdits}>Guardar cambios</button>
                <button type="button" className="px-3 py-1 rounded bg-green-600 text-white inline-flex items-center gap-1" onClick={approveFromEdit}>
                  <CheckCircle size={14} /> Aprobar ahora
                </button>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal de rechazo de pedido */}
      <Modal isOpen={isRejectOpen} onClose={closeReject} title={`Rechazar pedido #${rejecting?.id || ''}`} size="max-w-md">
        {isRejectOpen && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <div>Fecha: {rejecting?.date}</div>
              <div>Proyecto: {rejecting?.project || '-'}</div>
              <div>Solicitante: {rejecting?.requesterName || rejecting?.requesterId}</div>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Motivo del rechazo</label>
              <textarea
                className="w-full border rounded px-2 py-1 text-sm"
                rows={3}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ej: No corresponde al proyecto, error en solicitud, etc."
              />
            </div>
            <div className="flex items-center justify-end gap-2">
              <button type="button" className="px-3 py-1 rounded bg-gray-200 text-gray-800 hover:bg-gray-300" onClick={closeReject}>Cancelar</button>
              <button type="button" className="px-3 py-1 rounded bg-red-600 text-white inline-flex items-center gap-1 hover:bg-red-700" onClick={confirmReject}>
                <XCircle size={14} /> Confirmar rechazo
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
